import { useState } from 'react';
import { Link } from 'react-router-dom';

export function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d7bmhtiqofe8vli40l30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-stone-900 text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/7f4f4933-43ac-46f9-a6f6-a6a961fa216c.png"
                alt="ZEPHYRION LLC"
                className="h-10 w-auto object-contain brightness-0 invert"
              />
              <span className="font-bold text-lg tracking-widest" style={{ fontFamily: "'Playfair Display', serif" }}>
                ZEPHYRION
              </span>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed mb-5">
              Premium kitchen & dining essentials crafted for the modern home. Elevate every meal with ZEPHYRION.
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm" />
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 transition-colors cursor-pointer">
                <i className="ri-facebook-line text-sm" />
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 transition-colors cursor-pointer">
                <i className="ri-pinterest-line text-sm" />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4 text-stone-300">Shop</h4>
            <ul className="space-y-2.5">
              {['Cookware', 'Dinnerware', 'Cutlery', 'Storage', 'Appliances'].map(cat => (
                <li key={cat}>
                  <Link to={`/products?category=${cat}`} className="text-stone-400 text-sm hover:text-amber-400 transition-colors">
                    {cat}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4 text-stone-300">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-stone-400 text-sm hover:text-amber-400 transition-colors">About Us</Link></li>
              <li><a href="mailto:zephyrionllc@outlook.com" className="text-stone-400 text-sm hover:text-amber-400 transition-colors">Contact</a></li>
              <li><Link to="/products" className="text-stone-400 text-sm hover:text-amber-400 transition-colors">All Products</Link></li>
            </ul>
            <div className="mt-6 space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-map-pin-line text-amber-400 text-xs" />
                </div>
                <p className="text-stone-400 text-xs leading-relaxed">4325 E Waltann Ln,<br />Phoenix, AZ 85032</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-amber-400 text-xs" />
                </div>
                <a href="mailto:zephyrionllc@outlook.com" className="text-stone-400 text-xs hover:text-amber-400 transition-colors">
                  zephyrionllc@outlook.com
                </a>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4 text-stone-300">Newsletter</h4>
            <p className="text-stone-400 text-sm mb-4 leading-relaxed">
              Subscribe for exclusive deals, recipes, and kitchen inspiration.
            </p>
            {submitted ? (
              <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl px-4 py-3">
                <p className="text-amber-400 text-sm font-medium">Thank you for subscribing!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 bg-stone-800 border border-stone-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-amber-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-10 h-10 flex items-center justify-center bg-amber-500 hover:bg-amber-600 rounded-xl transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line text-white" />
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-stone-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-500 text-xs">
            © 2025 ZEPHYRION LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-stone-500 text-xs">Privacy Policy</span>
            <span className="text-stone-500 text-xs">Terms of Service</span>
            <div className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-stone-500 text-xs" />
              <span className="text-stone-500 text-xs">Secure Checkout</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
